import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useTransitionState } from 'react-transition-state';

import { TransitionState } from './types';

type DrawerControllerProps = {
  isOpen?: boolean;
  children: ReactNode;
};

const DrawerControllerContext = React.createContext<null | TransitionState>(
  null
);

export const DrawerControllerContextProvider = DrawerControllerContext.Provider;

export const useDrawerControllerContext = () => {
  const context = useContext(DrawerControllerContext);
  if (!context) {
    throw new Error(
      'Drawers must be wrapped in a <DrawerController>. You should generally do this outside of the component that renders the <Drawer> or <TabbedDrawer>. See https://reckon-web-design-system.netlify.app/package/drawer#drawercontroller for how to use it and why.'
    );
  }

  return context;
};

/** @deprecated The Drawer package has been deprecated. Use the SideDrawer package instead. */
export const DrawerController = ({
  isOpen = false,
  children,
}: DrawerControllerProps) => {
  const [animationTimeout] = useState(150);

  const [transitionState, toggle] = useTransitionState({
    timeout: animationTimeout,
    initialEntered: isOpen,
    mountOnEnter: true,
    unmountOnExit: true,
  });

  useEffect(() => {
    toggle(isOpen);
  }, [isOpen, toggle]);

  // Optional: Re-toggle if timeout changes mid-transition
  useEffect(() => {
    if (
      transitionState.status === 'entering' ||
      transitionState.status === 'exiting'
    ) {
      toggle(isOpen);
    }
  }, [animationTimeout, isOpen, toggle, transitionState.status]);

  if (!transitionState.isMounted) {
    return null;
  }

  // Map extended states to the legacy TransitionState
  const mapTransitionState = (
    status: typeof transitionState.status
  ): TransitionState => {
    switch (status) {
      case 'preEnter':
        return 'entering';
      case 'preExit':
        return 'exiting';
      default:
        return status as TransitionState;
    }
  };

  return (
    <DrawerControllerContextProvider
      value={mapTransitionState(transitionState.status)}
    >
      {children}
    </DrawerControllerContextProvider>
  );
};
